import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PrinterIcon } from '@heroicons/react/24/outline'; // Removed ArrowDownTrayIcon
import axiosInstance from '../../api/axiosConfig';
import axios from 'axios'; // Keep for public fetch
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
// Removed jsPDF imports

// Interface matches the data returned by /structures/active endpoint
interface Structure {
  id: number;
  code: string;
  type: string;
  name: string; // This field is constructed in the backend controller
  address: string; // Mapped from location
  size: string; // Mapped from dimensions or screenSize
  currentDailyPrice: number;
  currentMonthlyPrice: number;
  isActive: boolean; // Always true for this endpoint, but kept for consistency
}

interface PriceListProps {
  isPublic?: boolean;
}

const PriceList: React.FC<PriceListProps> = ({ isPublic = false }) => {
  // const [structures, setStructures] = useState<Structure[]>([]); // Removed unused state
  const [groupedStructures, setGroupedStructures] = useState<Record<string, Structure[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState<string>('');
  const priceListRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const fetchStructures = async () => {
      setLoading(true);
      try {
        // Use a different endpoint for public view that doesn't require authentication
        const endpoint = isPublic ? '/api/structures/active' : '/structures/active';
        
        // For public access, use direct axios instead of the configured one with auth tokens
        const response = isPublic 
          ? await axios.get(window.location.origin + endpoint)
          : await axiosInstance.get(endpoint);
          
        // setStructures(response.data); // Removed unused state update
        
        // Group structures by type
        const grouped = response.data.reduce((acc: Record<string, Structure[]>, structure: Structure) => {
          if (!acc[structure.type]) {
            acc[structure.type] = [];
          }
          acc[structure.type].push(structure);
          return acc;
        }, {});
        
        setGroupedStructures(grouped);
        
        // Set current date
        const now = new Date();
        setCurrentDate(now.toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }));
      } catch (error) {
        console.error('Error fetching structures:', error);
        setError('Ошибка при загрузке данных о конструкциях');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStructures();
  }, [isPublic]);
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Open printable view in a new tab
  const handlePrint = () => {
    // Construct the URL for the print page
    // Note: This uses the backend endpoint which fetches current data,
    // ensuring the printed version is up-to-date.
    const printUrl = `/structures/price-list/print`; // Use a dedicated print route
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Removed downloadPdf function

  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Прайс-лист"
        subtitle={`Цены действительны на ${currentDate}`}
        backLink="/structures"
        actions={
          <>
            <button
              type="button"
              onClick={handlePrint}
              className="btn-secondary flex items-center"
            >
              <PrinterIcon className="h-5 w-5 mr-1" />
              Печать
            </button>
            {/* Removed PDF download button */}
          </>
        }
      />
      
      {error ? (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      ) : (
        <div ref={priceListRef} className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-0">
          <div className="print:hidden mb-6">
            <h1 className="text-2xl font-bold text-center">Прайс-лист на рекламные конструкции</h1>
            <p className="text-center text-gray-500">Действителен на {currentDate}</p>
          </div>
          
          {Object.keys(groupedStructures).length === 0 ? (
            <p className="text-center text-gray-500">Нет активных конструкций</p>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedStructures).map(([type, structures]) => (
                <div key={type}>
                  <h2 className="text-xl font-bold mb-3">{type}</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Код</th>
                          <th className="table-header-cell">Название</th>
                          <th className="table-header-cell">Адрес</th>
                          <th className="table-header-cell">Размер</th>
                          <th className="table-header-cell">Цена/день</th>
                          <th className="table-header-cell">Цена/месяц</th>
                          <th className="table-header-cell print:hidden">Действия</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {structures.map((structure) => (
                          <tr key={structure.id}>
                            <td className="table-cell">{structure.code}</td>
                            {/* Added break-words */}
                            <td className="table-cell break-words">{structure.name}</td>
                            {/* Added break-words */}
                            <td className="table-cell break-words">{structure.address}</td>
                            <td className="table-cell">{structure.size}</td>
                            <td className="table-cell">{formatCurrency(structure.currentDailyPrice)}</td>
                            <td className="table-cell">{formatCurrency(structure.currentMonthlyPrice)}</td>
                            <td className="table-cell print:hidden">
                              <Link 
                                to={`/structures/${structure.id}`} 
                                className="text-primary hover:text-primary-dark"
                              >
                                Подробнее
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              
              <div className="mt-8 text-sm text-gray-500 print:mt-2 print:text-xs">
                <p>Цены указаны в тенге с учетом НДС. Для получения информации о скидках и специальных предложениях обращайтесь к менеджерам.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceList;
